import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useRoute } from '../../context/RouteContext';
import { useAuth } from '../../context/AuthContext';
import { fetchPublishedRoutes, fetchUserRoutes, fetchAllPublishedRoutes, fetchRoutesByCountry, fetchRoutesByUsers } from '../../services/publishService';
import { fetchPublishedTours, fetchMyTours, fetchToursByCountry, fetchToursByUsers } from '../../services/tourService';
import { fetchFollowingUids } from '../../services/followService';
import { syncTourStatus } from '../../services/stripeService';
import { formatDistance, formatDuration } from '../../utils/formatters';
import { haversineKm } from '../../utils/geomath';
import { slugify } from '../../utils/geocoding';
import { RouteDetailModal } from './RouteDetailModal';
import { TourBuilder } from '../Tours/TourBuilder';
import { BusinessApplicationModal } from '../Profile/BusinessApplicationModal';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { TourDetailModal } from '../Tours/TourDetailModal';
import { CurvinessBadge } from '../common/CurvinessBadge';
import { RouteThumbnail } from '../common/RouteThumbnail';
import { computeCurviness } from '../../utils/curviness';
import { isRecurring, formatRunsOn, nextOccurrence, isTourPast } from '../../utils/tourSchedule';
import './CommunityPanel.css';

// Deterministic gradient from route name
const BANNERS = [
  ['#0f2027','#2c5364'],
  ['#1a1a2e','#0f3460'],
  ['#134e4a','#0d2b29'],
  ['#1e1b4b','#2d2a6e'],
  ['#292524','#44403c'],
  ['#0c1a2e','#1a3a5c'],
  ['#1f2d1f','#2d4a2d'],
  ['#2d1b1b','#4a2d2d'],
];
function routeBanner(name = '') {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0xffff;
  return BANNERS[h % BANNERS.length];
}

function cityOf(name) {
  if (!name) return '';
  return name.split(',')[0].trim();
}

// Small verified-business trust icon next to a card's author name —
// `badge` is the { businessVerified } snapshot taken at publish time (tours:
// tourService.createTourDraft; routes: publishService).
function CreatorBadges({ badge }) {
  if (!badge?.businessVerified) return null;
  return (
    <span className="cc__creator-badges">
      <span className="cc__creator-badge cc__creator-badge--business" title="Verified business">
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-3"/></svg>
      </span>
    </span>
  );
}

const DIST_OPTIONS = [
  { label: '25 km',  km: 25  },
  { label: '50 km',  km: 50  },
  { label: '100 km', km: 100 },
  { label: '250 km', km: 250 },
  { label: 'Any',    km: null },
];

export function CommunityPanel({ onOpenProfile, onOpenCategory }) {
  const { showToast, dispatch } = useRoute();
  const { user, profile } = useAuth();
  const [communityTab, setCommunityTab] = useState('routes');
  const [showBusinessApply, setShowBusinessApply] = useState(false);
  // "Following" filter — only content from people you follow (both tabs).
  const [followingOnly, setFollowingOnly] = useState(false);
  const [followingUids, setFollowingUids] = useState(null); // null = not loaded yet
  const isVerifiedBusiness = profile?.businessVerifiedStatus === 'verified';

  // Normal paginated routes
  const [routes, setRoutes]           = useState([]);
  const [cursor, setCursor]           = useState(null);
  const [hasMore, setHasMore]         = useState(false);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]             = useState('');
  const [selected, setSelected]       = useState(null);
  const [filterUser, setFilterUser]   = useState(null);
  const [myRoutes,     setMyRoutes]     = useState([]);
  const [myRoutesOpen, setMyRoutesOpen] = useState(true);

  // Tours tab state
  const [tours,          setTours]          = useState([]);
  const [toursLoading,   setToursLoading]   = useState(false);
  const [toursError,     setToursError]     = useState('');
  const [myTours,        setMyTours]        = useState([]);
  const [myToursOpen,    setMyToursOpen]    = useState(true);
  const [selectedTour,   setSelectedTour]   = useState(null);
  const [editTour,       setEditTour]       = useState(null); // tour being edited
  const [showTourBuilder, setShowTourBuilder] = useState(false);

  // Nearby mode
  const [nearbyMode, setNearbyMode]   = useState(false);
  const [userPos, setUserPos]         = useState(null);
  const [allRoutes, setAllRoutes]     = useState([]);
  const [maxDistKm, setMaxDistKm]     = useState(100);
  const [geoLoading, setGeoLoading]   = useState(false);
  const [geoError, setGeoError]       = useState('');

  // Discovery filters
  const [routeQuery, setRouteQuery] = useState('');
  const [routeSort,  setRouteSort]  = useState('newest');   // nearest|newest|curviness|distance|distanceDesc|rating
  const [routeCurv,  setRouteCurv]  = useState('any');      // any|flowing|twisty|very
  const [routeLen,   setRouteLen]   = useState('any');      // any|short|med|long|epic
  const [tourQuery,  setTourQuery]  = useState('');
  const [tourSort,   setTourSort]   = useState('soonest');  // nearest|soonest|curviness|distance
  const [tourPrice,  setTourPrice]  = useState('all');      // all|free|paid
  const [tourSkill,  setTourSkill]  = useState('all');      // all|beginner|intermediate|expert
  const [tourLen,    setTourLen]    = useState('any');
  const [tourCategory, setTourCategory] = useState('all');  // all|tour|lesson

  // Location-aware discovery (per-tab country so each dropdown only lists
  // countries that actually have that kind of content).
  const [routeCountry,   setRouteCountry]   = useState('');  // '' = all
  const [tourCountry,    setTourCountry]    = useState('');
  const [routeCountries, setRouteCountries] = useState([]);  // [{slug,name}]
  const [tourCountries,  setTourCountries]  = useState([]);
  const [userGeo,        setUserGeo]        = useState(null); // { slug, lat, lng }
  const geoDefaultDone = useRef(false);
  // Set right before the auto country-detect effect changes routeCountry/tourCountry,
  // so the resulting reload can skip the loading-spinner reset and avoid blanking
  // out a list that's already on screen.
  const silentRouteReload = useRef(false);
  const silentTourReload  = useRef(false);
  const routesReqId = useRef(0);
  const toursReqId  = useRef(0);

  // Detect the visitor's country/coords (IP-based, no permission prompt).
  useEffect(() => {
    fetch('https://ipwho.is/')
      .then(r => r.json())
      .then(d => {
        if (d && d.success !== false && d.latitude != null) {
          setUserGeo({ slug: slugify(d.country || ''), lat: d.latitude, lng: d.longitude });
        }
      })
      .catch(() => {});
  }, []);

  // Build the per-tab country dropdown options once.
  useEffect(() => {
    const distinct = (arr) => {
      const m = new Map();
      for (const x of arr) if (x.countrySlug && x.country && !m.has(x.countrySlug)) m.set(x.countrySlug, x.country);
      return [...m.entries()].map(([slug, name]) => ({ slug, name })).sort((a, b) => a.name.localeCompare(b.name));
    };
    Promise.all([
      fetchAllPublishedRoutes().catch(() => []),
      fetchPublishedTours().then(r => r.tours).catch(() => []),
    ]).then(([rs, ts]) => {
      setRouteCountries(distinct(rs));
      setTourCountries(distinct(ts));
    });
  }, []);

  // Default each tab to the visitor's country + nearest-first sort (once).
  useEffect(() => {
    if (geoDefaultDone.current || !userGeo || (!routeCountries.length && !tourCountries.length)) return;
    geoDefaultDone.current = true;
    if (routeCountries.some(c => c.slug === userGeo.slug)) { silentRouteReload.current = true; setRouteCountry(userGeo.slug); setRouteSort('nearest'); }
    if (tourCountries.some(c => c.slug === userGeo.slug))  { silentTourReload.current = true;  setTourCountry(userGeo.slug);  setTourSort('nearest'); }
  }, [userGeo, routeCountries, tourCountries]);

  // Handle return from Stripe Checkout after publishing a tour
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tourId = params.get('tour_published');
    if (!tourId || !user) return;
    // Remove the query param immediately to avoid re-running on refresh
    window.history.replaceState({}, '', window.location.pathname);
    syncTourStatus(tourId)
      .then(published => {
        if (published) showToast('Tour published successfully!', 'success');
        else showToast('Payment confirmed — your tour is being published.', 'info');
      })
      .catch(() => {});
  }, [user]);  // eslint-disable-line

  // Who the signed-in user follows (for the "Following" filter).
  useEffect(() => {
    if (!user) { setFollowingUids(null); setFollowingOnly(false); return; }
    fetchFollowingUids(user.uid).then(setFollowingUids).catch(() => setFollowingUids([]));
  }, [user]);

  // Load tours when tab is active (server-side by country when a country is set)
  useEffect(() => {
    if (communityTab !== 'tours') return;
    const reqId = ++toursReqId.current;
    const silent = silentTourReload.current;
    silentTourReload.current = false;
    if (!silent) setToursLoading(true);
    setToursError('');
    // Following mode ignores the country filter — "everything from people I follow".
    const load = followingOnly
      ? (followingUids === null ? Promise.resolve([]) : fetchToursByUsers(followingUids))
      : tourCountry
        ? fetchToursByCountry(tourCountry)
        : fetchPublishedTours().then(r => r.tours);
    load
      .then(t => { if (toursReqId.current === reqId) setTours(t); })
      .catch(err => { if (toursReqId.current === reqId) setToursError(err.message); })
      .finally(() => { if (toursReqId.current === reqId) setToursLoading(false); });
  }, [communityTab, tourCountry, followingOnly, followingUids]);

  // Load user's own tours when tab is active and signed in
  useEffect(() => {
    if (communityTab !== 'tours' || !user) return;
    fetchMyTours(user.uid).then(setMyTours).catch(() => {});
  }, [communityTab, user]);

  // Load user's own published routes when tab is active and signed in
  // (mirrors My Tours above — not shown while already filtered to a
  // specific user's routes, since that view already IS one user's routes).
  useEffect(() => {
    if (communityTab !== 'routes' || !user || filterUser) return;
    fetchUserRoutes(user.uid).then(setMyRoutes).catch(() => {});
  }, [communityTab, user, filterUser]);

  useEffect(() => {
    if (communityTab !== 'routes' || nearbyMode) return;
    const reqId = ++routesReqId.current;
    const silent = silentRouteReload.current;
    silentRouteReload.current = false;
    if (!silent) setLoading(true);
    setError('');
    if (followingOnly) {
      (followingUids === null ? Promise.resolve([]) : fetchRoutesByUsers(followingUids))
        .then(r => { if (routesReqId.current === reqId) { setRoutes(r); setCursor(null); setHasMore(false); } })
        .catch(err => { if (routesReqId.current === reqId) setError(err.message); })
        .finally(() => { if (routesReqId.current === reqId) setLoading(false); });
    } else if (filterUser) {
      fetchUserRoutes(filterUser.uid)
        .then(r => { if (routesReqId.current === reqId) { setRoutes(r); setCursor(null); setHasMore(false); } })
        .catch(err => { if (routesReqId.current === reqId) setError(err.message); })
        .finally(() => { if (routesReqId.current === reqId) setLoading(false); });
    } else if (routeCountry) {
      fetchRoutesByCountry(routeCountry)
        .then(r => { if (routesReqId.current === reqId) { setRoutes(r); setCursor(null); setHasMore(false); } })
        .catch(err => { if (routesReqId.current === reqId) setError(err.message); })
        .finally(() => { if (routesReqId.current === reqId) setLoading(false); });
    } else {
      fetchPublishedRoutes()
        .then(({ routes: r, lastDoc, hasMore }) => { if (routesReqId.current === reqId) { setRoutes(r); setCursor(lastDoc); setHasMore(hasMore); } })
        .catch(err => { if (routesReqId.current === reqId) setError(err.message); })
        .finally(() => { if (routesReqId.current === reqId) setLoading(false); });
    }
  }, [filterUser, communityTab, nearbyMode, routeCountry, followingOnly, followingUids]);

  async function loadMore() {
    if (filterUser || followingOnly) return;
    setLoadingMore(true);
    try {
      const { routes: more, lastDoc, hasMore: hm } = await fetchPublishedRoutes(cursor);
      setRoutes(prev => [...prev, ...more]);
      setCursor(lastDoc);
      setHasMore(hm);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoadingMore(false);
    }
  }

  async function activateNearby() {
    if (!navigator.geolocation) { setGeoError('Geolocation not supported by your browser.'); return; }
    setGeoError('');
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async pos => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        setUserPos({ lat, lng });
        setFollowingOnly(false);
        setNearbyMode(true);
        try {
          const all = await fetchAllPublishedRoutes();
          setAllRoutes(all);
        } catch (err) {
          setGeoError('Could not load routes: ' + err.message);
        } finally {
          setGeoLoading(false);
        }
      },
      () => {
        setGeoError('Location access denied. Please allow location in your browser settings.');
        setGeoLoading(false);
      },
      { timeout: 10_000, maximumAge: 60_000 }
    );
  }

  function deactivateNearby() {
    setNearbyMode(false);
    setUserPos(null);
    setAllRoutes([]);
    setGeoError('');
  }

  // Computed nearby list
  const nearbyRoutes = useMemo(() => {
    if (!nearbyMode || !userPos) return [];
    return allRoutes
      .map(r => {
        const wp = r.waypoints?.[0];
        const lat = r.startLat ?? wp?.lat;
        const lng = r.startLng ?? wp?.lng;
        const distKm = lat != null ? haversineKm(userPos.lat, userPos.lng, lat, lng) : Infinity;
        return { ...r, _distKm: distKm };
      })
      .filter(r => maxDistKm == null || r._distKm <= maxDistKm)
      .sort((a, b) => a._distKm - b._distKm);
  }, [nearbyMode, userPos, allRoutes, maxDistKm]);

  const displayedRoutes = nearbyMode ? nearbyRoutes : routes;
  const isFiltered = nearbyMode || !!filterUser;


  // ── Discovery: filter + sort (client-side over the loaded list) ──
  const CURV_MIN = { any: -1, flowing: 1, twisty: 2, very: 3 };
  const inLength = (m, f) => {
    if (f === 'any' || m == null) return true;
    const km = m / 1000;
    if (f === 'short') return km < 50;
    if (f === 'med')   return km >= 50 && km < 150;
    if (f === 'long')  return km >= 150 && km < 300;
    if (f === 'epic')  return km >= 300;
    return true;
  };
  const distFromUser = (lat, lng) =>
    (userGeo && lat != null && lng != null) ? haversineKm(userGeo.lat, userGeo.lng, lat, lng) : Infinity;

  const routeCurvCache = useMemo(() => {
    const m = new Map();
    for (const r of displayedRoutes) if (!m.has(r.id)) m.set(r.id, computeCurviness(r.geometry));
    return m;
  }, [displayedRoutes]);

  const visibleRoutes = useMemo(() => {
    let arr = [...displayedRoutes];
    const q = routeQuery.trim().toLowerCase();
    if (q) arr = arr.filter(r => `${r.name || ''} ${r.startName || ''} ${r.endName || ''}`.toLowerCase().includes(q));
    if (routeCurv !== 'any')   arr = arr.filter(r => (routeCurvCache.get(r.id)?.level ?? -1) >= CURV_MIN[routeCurv]);
    if (routeLen !== 'any')     arr = arr.filter(r => inLength(r.distance, routeLen));
    const curv = r => routeCurvCache.get(r.id)?.degPerKm ?? 0;
    switch (routeSort) {
      case 'nearest':      arr.sort((a, b) => distFromUser(a.startLat, a.startLng) - distFromUser(b.startLat, b.startLng)); break;
      case 'distance':     arr.sort((a, b) => (a.distance || 0) - (b.distance || 0)); break;
      case 'distanceDesc': arr.sort((a, b) => (b.distance || 0) - (a.distance || 0)); break;
      case 'curviness':    arr.sort((a, b) => curv(b) - curv(a)); break;
      case 'rating':       arr.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0)); break;
      default: break; // newest = fetch order; nearby keeps distance order
    }
    return arr;
  }, [displayedRoutes, routeQuery, routeCurv, routeLen, routeSort, routeCurvCache, userGeo]); // eslint-disable-line

  // "My Tours" sits under the "Upcoming Tours" header, so it should honour that —
  // isTourPast already treats a recurring tour as upcoming for as long as its
  // season still has run days left, so this doesn't hide active recurring tours.
  const myUpcomingTours = useMemo(() => myTours.filter(t => !isTourPast(t)), [myTours]);

  const tourCurvCache = useMemo(() => {
    const m = new Map();
    for (const t of tours) if (!m.has(t.id)) m.set(t.id, computeCurviness(t.geometry));
    return m;
  }, [tours]);

  const tourStartMs = t => { const d = t.startDate?.toDate ? t.startDate.toDate() : null; return d ? d.getTime() : Infinity; };

  const visibleTours = useMemo(() => {
    // Hide tours that have already happened — they can't be booked.
    let arr = tours.filter(t => !isTourPast(t));
    const q = tourQuery.trim().toLowerCase();
    if (q) arr = arr.filter(t => `${t.name || ''} ${t.meetingPoint?.name || ''} ${t.startName || ''}`.toLowerCase().includes(q));
    if (tourPrice === 'free') arr = arr.filter(t => t.registrationType !== 'paid');
    if (tourPrice === 'paid') arr = arr.filter(t => t.registrationType === 'paid');
    if (tourSkill !== 'all')  arr = arr.filter(t => t.skillLevel === tourSkill);
    if (tourLen !== 'any')    arr = arr.filter(t => inLength(t.distance, tourLen));
    // Existing tours predate the category field — treat missing as 'tour'.
    if (tourCategory === 'tour')   arr = arr.filter(t => (t.category || 'tour') === 'tour');
    if (tourCategory === 'lesson') arr = arr.filter(t => t.category === 'lesson');
    const curv = t => tourCurvCache.get(t.id)?.degPerKm ?? 0;
    switch (tourSort) {
      case 'nearest':   arr.sort((a, b) => distFromUser(a.startLat, a.startLng) - distFromUser(b.startLat, b.startLng)); break;
      case 'distance':  arr.sort((a, b) => (a.distance || 0) - (b.distance || 0)); break;
      case 'curviness': arr.sort((a, b) => curv(b) - curv(a)); break;
      default:          arr.sort((a, b) => tourStartMs(a) - tourStartMs(b)); break; // soonest
    }
    return arr;
  }, [tours, tourQuery, tourPrice, tourSkill, tourLen, tourCategory, tourSort, tourCurvCache, userGeo]); // eslint-disable-line

  // Keep list cards in sync with the live count from the detail modal.
  // Guarded so we only create new state when the value actually changed
  // (prevents a re-render → re-subscribe loop).
  const handleTourCountChange = useCallback((tourId, count) => {
    const apply = arr => {
      const t = arr.find(x => x.id === tourId);
      if (!t || t.registrationCount === count) return arr;
      return arr.map(x => x.id === tourId ? { ...x, registrationCount: count } : x);
    };
    setTours(apply);
    setMyTours(apply);
  }, []);

  function handleUnpublished(routeId) {
    setRoutes(prev => prev.filter(r => r.id !== routeId));
    setAllRoutes(prev => prev.filter(r => r.id !== routeId));
    setMyRoutes(prev => prev.filter(r => r.id !== routeId));
    dispatch({ type: 'CLEAR_PUBLISHED', payload: routeId });
    showToast('Route unpublished', 'info');
  }

  return (
    <div className="community">
      {/* Tab switcher */}
      <div className="community__tabs">
        <button
          className={`community__tab${communityTab === 'routes' ? ' community__tab--active' : ''}`}
          onClick={() => { setCommunityTab('routes'); setFilterUser(null); deactivateNearby(); }}
        >
          Routes
        </button>
        <button
          className={`community__tab${communityTab === 'tours' ? ' community__tab--active' : ''}`}
          onClick={() => setCommunityTab('tours')}
        >
          Tours
        </button>
      </div>


      {communityTab === 'tours' && (
        <div className="tours">
          {/* Header */}
          <div className="tours__header">
            <span className="tours__title">Upcoming Tours</span>
            <div className="tours__header-actions">
              {user && (
                <button
                  className={`community__follow-toggle${followingOnly ? ' community__follow-toggle--active' : ''}`}
                  onClick={() => setFollowingOnly(v => !v)}
                  title="Only tours from people you follow"
                  aria-pressed={followingOnly}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  Following
                </button>
              )}
              {isVerifiedBusiness && (
                <button className="tours__create-btn" onClick={() => { setEditTour(null); setShowTourBuilder(true); }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Create Tour
                </button>
              )}
            </div>
          </div>

          {user && !isVerifiedBusiness && (
            <div className="tours__apply-banner">
              <span>Own a touring business or coach riders?</span>
              <button className="tours__apply-banner-btn" onClick={() => setShowBusinessApply(true)}>
                Apply to create tours &amp; lessons
              </button>
            </div>
          )}

          {/* My Tours section */}
          {user && myUpcomingTours.length > 0 && (
            <div className="tours__my">
              <button className="tours__my-title tours__my-toggle" onClick={() => setMyToursOpen(v => !v)}>
                <span>My Tours <span className="tours__my-count">{myUpcomingTours.length}</span></span>
                <svg className={`tours__my-chevron${myToursOpen ? ' tours__my-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {myToursOpen && (
              <ul className="tours__my-list">
                {myUpcomingTours.map(t => {
                  const statusColor = { published: '#16a34a', draft: '#d97706', pending_payment: '#d97706', cancelled: '#6b7280' }[t.status] ?? '#6b7280';
                  const statusLabel = { published: 'Published', draft: 'Draft', pending_payment: 'Pending payment', cancelled: 'Cancelled' }[t.status] ?? t.status;
                  const recurringMy = isRecurring(t);
                  const startD = recurringMy ? nextOccurrence(t) : (t.startDate?.toDate ? t.startDate.toDate() : null);
                  const myDateLabel = recurringMy
                    ? formatRunsOn(t.runsOn)
                    : (startD ? startD.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
                  return (
                    <li key={t.id} className="tours__my-row" onClick={() => setSelectedTour(t)}>
                      <div className="tours__my-info">
                        <span className="tours__my-name">{t.name}</span>
                        <span className="tours__my-date">{myDateLabel}</span>
                      </div>
                      <span className="tours__my-status" style={{ color: statusColor, borderColor: statusColor, background: statusColor + '14' }}>{statusLabel}</span>
                    </li>
                  );
                })}
              </ul>
              )}
            </div>
          )}

          {toursLoading && (
            <div className="community__loading"><span className="community__spinner" /> Loading tours…</div>
          )}
          {!toursLoading && toursError && (
            <div className="community__error">{toursError}</div>
          )}
          {/* Filter bar stays visible (even when empty) so you can change country */}
          {!toursLoading && !toursError && (tours.length > 0 || tourCountry) && (
            <div className="cc-filters">
              <div className="cc-filters__search">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input type="text" placeholder="Search tours or places…" value={tourQuery} onChange={e => setTourQuery(e.target.value)} />
                {tourQuery && <button className="cc-filters__clear" onClick={() => setTourQuery('')} aria-label="Clear">×</button>}
              </div>
              <div className="cc-filters__selects">
                <select value={tourSort} onChange={e => setTourSort(e.target.value)}>
                  {userGeo && <option value="nearest">Nearest</option>}
                  <option value="soonest">Soonest</option>
                  <option value="curviness">Curviest</option>
                  <option value="distance">Shortest</option>
                </select>
                <select value={tourCategory} onChange={e => setTourCategory(e.target.value)}>
                  <option value="all">Tours &amp; lessons</option>
                  <option value="tour">Tours only</option>
                  <option value="lesson">Lessons only</option>
                </select>
                <select value={tourCountry} onChange={e => setTourCountry(e.target.value)}>
                  <option value="">All countries</option>
                  {tourCountries.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
                <select value={tourPrice} onChange={e => setTourPrice(e.target.value)}>
                  <option value="all">Any price</option>
                  <option value="free">Free</option>
                  <option value="paid">Paid</option>
                </select>
                <select value={tourSkill} onChange={e => setTourSkill(e.target.value)}>
                  <option value="all">Any level</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="expert">Expert</option>
                </select>
                <select value={tourLen} onChange={e => setTourLen(e.target.value)}>
                  <option value="any">Any length</option>
                  <option value="short">&lt; 50 km</option>
                  <option value="med">50–150 km</option>
                  <option value="long">150–300 km</option>
                  <option value="epic">300 km +</option>
                </select>
              </div>
            </div>
          )}

          {!toursLoading && !toursError && tours.length === 0 && (
            <div className="community__empty">
              <div className="community__empty-icon">🏍</div>
              <div className="community__empty-title">{followingOnly ? 'No tours from people you follow yet' : tourCountry ? 'No tours here yet' : 'No tours yet'}</div>
              <div className="community__empty-sub">{followingOnly ? 'Follow riders and businesses from their profiles to see their new tours here.' : tourCountry ? 'Try another country, or “All countries”.' : 'Be the first to create a guided tour!'}</div>
            </div>
          )}

          {!toursLoading && !toursError && tours.length > 0 && (
            visibleTours.length === 0 ? (
              <div className="community__empty community__empty--mini">No tours match your filters.</div>
            ) : (
              <ul className="community__list">
                {visibleTours.map(tour => {
                const hasImg = tour.imageUrls?.length > 0;
                const [g1, g2] = routeBanner(tour.name);
                const skill = { beginner: '#16a34a', intermediate: '#d97706', expert: '#dc2626' }[tour.skillLevel] ?? '#d97706';
                const recurring = isRecurring(tour);
                const startD = recurring ? nextOccurrence(tour) : (tour.startDate?.toDate ? tour.startDate.toDate() : null);
                const dateLabel = recurring
                  ? formatRunsOn(tour.runsOn)
                  : (startD ? startD.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
                // Recurring tours run weekly with no fixed total cap.
                const isFull = !recurring && tour.maxParticipants && tour.registrationCount >= tour.maxParticipants;
                const spotsLeft = !recurring && tour.maxParticipants ? tour.maxParticipants - (tour.registrationCount || 0) : null;

                return (
                  <li
                    key={tour.id}
                    className="cc"
                    onClick={() => setSelectedTour(tour)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && setSelectedTour(tour)}
                  >
                    {/* Banner */}
                    <div
                      className="cc__banner"
                      style={hasImg ? undefined : { background: `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)` }}
                    >
                      {hasImg
                        ? <img src={tour.imageUrls[0]} alt="" loading="lazy" className="cc__banner-img" />
                        : <span className="cc__banner-initial">{tour.name[0]?.toUpperCase()}</span>
                      }
                      <div className="cc__banner-overlay" />

                      {/* Date badge */}
                      <div className="cc__banner-badges">
                        <span className="cc__badge">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                          {dateLabel}
                        </span>
                        {tour.category === 'lesson' && (
                          <span className="cc__badge cc__badge--lesson">
                            Lesson
                          </span>
                        )}
                        {tour.category === 'lesson' && tour.learnerFriendly && (
                          <span className="cc__badge cc__badge--learner" title="Suitable for learner-licence riders">L</span>
                        )}
                        {user?.uid === tour.userId && <span className="cc__badge cc__badge--own">Yours</span>}
                      </div>

                      {/* Price badge */}
                      <div className="cc__banner-rating" style={{ color: tour.registrationType === 'paid' ? '#34d399' : '#a3e635' }}>
                        {tour.registrationType !== 'paid' ? 'Free'
                          : tour.category === 'lesson' ? `$${Number(tour.hourlyRate || 0).toFixed(2)}/hr`
                          : `$${tour.ticketPrice?.toFixed(2)}`}
                      </div>
                    </div>

                    {/* Card body */}
                    <div className="cc__body">
                      <div className="cc__name">{tour.name}</div>

                      <div className="cc__stats">
                        {tour.category !== 'lesson' && (
                          <span className="cc__stat" style={{ color: skill, background: 'transparent', paddingLeft: 0 }}>
                            ● {tour.skillLevel?.[0]?.toUpperCase() + tour.skillLevel?.slice(1)}
                          </span>
                        )}
                        {tour.distance != null && (
                          <span className="cc__stat">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.3 8.7 8.7 21.3c-1 1-2.5 1-3.4 0l-2.6-2.6c-1-1-1-2.5 0-3.4L15.3 2.7c1-1 2.5-1 3.4 0l2.6 2.6c1 1 1 2.5 0 3.4Z"/></svg>
                            {formatDistance(tour.distance)}
                          </span>
                        )}
                        {tour.category === 'lesson' && tour.rentBikeAvailable && (
                          <span className="cc__stat" title="Bike hire available">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="17" r="3"/><circle cx="19" cy="17" r="3"/><path d="M9 17l2-9h3l3 5.5"/><path d="M14 8l2-3h3"/><path d="M5 14l4-6"/></svg>
                            ${Number(tour.bikeHourlyRate || 0).toFixed(2)}/hr bike
                          </span>
                        )}
                        {tour.ratingCount > 0 && tour.averageRating != null && (
                          <span className="cc__stat" style={{ color: '#f5a623' }}>
                            ★ {tour.averageRating.toFixed(1)} ({tour.ratingCount})
                          </span>
                        )}
                        {isFull
                          ? <span className="cc__stat" style={{ color: '#dc2626', background: '#fef2f2' }}>Full</span>
                          : spotsLeft != null && (
                            <span className="cc__stat">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                              {spotsLeft} spots left
                            </span>
                          )
                        }
                      </div>

                      <div className="cc__author">
                        {(tour.business ? tour.business.logoUrl : tour.userPhoto)
                          ? <img src={tour.business ? tour.business.logoUrl : tour.userPhoto} alt="" className="cc__avatar" />
                          : <span className="cc__avatar cc__avatar--initial">{(tour.business?.name ?? tour.userName)?.[0]?.toUpperCase() ?? '?'}</span>
                        }
                        <span className="cc__author-name">{tour.business?.name ?? tour.userName}</span>
                        <CreatorBadges badge={tour.creatorBadge} />
                        <span className="cc__view-arrow">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
              </ul>
            )
          )}
        </div>
      )}

      {communityTab === 'routes' && (
        <>
          {/* Nearby bar */}
          {!filterUser && (
            <div className="community__nearby-bar">
              <div className="community__filter-row">
                <button
                  className={`community__nearby-btn${nearbyMode ? ' community__nearby-btn--active' : ''}`}
                  onClick={nearbyMode ? deactivateNearby : activateNearby}
                  disabled={geoLoading}
                >
                  {geoLoading
                    ? <span className="community__spinner community__spinner--sm" />
                    : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
                        <path d="M12 2a10 10 0 0 1 10 10A10 10 0 0 1 12 22A10 10 0 0 1 2 12A10 10 0 0 1 12 2z" strokeOpacity="0.3"/>
                      </svg>
                  }
                  {nearbyMode ? 'Nearby on' : 'Near Me'}
                </button>

                {user && (
                  <button
                    className={`community__follow-toggle${followingOnly ? ' community__follow-toggle--active' : ''}`}
                    onClick={() => { if (!followingOnly && nearbyMode) deactivateNearby(); setFollowingOnly(v => !v); }}
                    title="Only routes from people you follow"
                    aria-pressed={followingOnly}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    Following
                  </button>
                )}
              </div>

              {nearbyMode && (
                <div className="community__dist-pills">
                  {DIST_OPTIONS.map(o => (
                    <button
                      key={o.label}
                      className={`community__dist-pill${maxDistKm === o.km ? ' community__dist-pill--active' : ''}`}
                      onClick={() => setMaxDistKm(o.km)}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* My Routes section */}
          {user && !filterUser && myRoutes.length > 0 && (
            <div className="tours__my">
              <button className="tours__my-title tours__my-toggle" onClick={() => setMyRoutesOpen(v => !v)}>
                <span>My Routes <span className="tours__my-count">{myRoutes.length}</span></span>
                <svg className={`tours__my-chevron${myRoutesOpen ? ' tours__my-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {myRoutesOpen && (
              <ul className="tours__my-list">
                {myRoutes.map(r => {
                  const publishedD = r.publishedAt?.toDate ? r.publishedAt.toDate() : (r.publishedAt?.seconds ? new Date(r.publishedAt.seconds * 1000) : null);
                  const myDateLabel = publishedD ? publishedD.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
                  return (
                    <li key={r.id} className="tours__my-row" onClick={() => setSelected(r)}>
                      <div className="tours__my-info">
                        <span className="tours__my-name">{r.name}</span>
                        <span className="tours__my-date">{myDateLabel}{r.distance ? ` · ${formatDistance(r.distance)}` : ''}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
              )}
            </div>
          )}

          {geoError && <div className="community__geo-error">{geoError}</div>}

          {(loading || geoLoading) && (
            <div className="community__loading">
              <span className="community__spinner" />
              {geoLoading ? 'Finding nearby routes…' : 'Loading routes…'}
            </div>
          )}

          {!loading && !geoLoading && error && (
            <div className="community__error">{error}</div>
          )}

          {!loading && !geoLoading && !error && (displayedRoutes.length > 0 || routeCountry || nearbyMode || filterUser || followingOnly) && (
            <div className="community__header">
              {filterUser ? (
                <>
                  <button className="community__back-btn" onClick={() => setFilterUser(null)}>← All routes</button>
                  <span className="community__title community__title--filter">By {filterUser.name}</span>
                </>
              ) : followingOnly ? (
                <>
                  <span className="community__title">From people you follow</span>
                  <span className="community__count">{routes.length}</span>
                </>
              ) : nearbyMode ? (
                <>
                  <span className="community__title">Nearby Routes</span>
                  <span className="community__count">{nearbyRoutes.length}</span>
                </>
              ) : (
                <>
                  <span className="community__title">Community Routes</span>
                  <span className="community__count">{routes.length}{hasMore ? '+' : ''}</span>
                </>
              )}
            </div>
          )}

          {!loading && !geoLoading && !error && (displayedRoutes.length > 0 || routeCountry) && (
            <div className="cc-filters">
              <div className="cc-filters__search">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input type="text" placeholder="Search routes or places…" value={routeQuery} onChange={e => setRouteQuery(e.target.value)} />
                {routeQuery && <button className="cc-filters__clear" onClick={() => setRouteQuery('')} aria-label="Clear">×</button>}
              </div>
              <div className="cc-filters__selects">
                <select value={routeSort} onChange={e => setRouteSort(e.target.value)}>
                  {userGeo && <option value="nearest">Nearest</option>}
                  <option value="newest">Newest</option>
                  <option value="curviness">Curviest</option>
                  <option value="distance">Shortest</option>
                  <option value="distanceDesc">Longest</option>
                  <option value="rating">Top rated</option>
                </select>
                <select value={routeCountry} onChange={e => setRouteCountry(e.target.value)}>
                  <option value="">All countries</option>
                  {routeCountries.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
                <select value={routeLen} onChange={e => setRouteLen(e.target.value)}>
                  <option value="any">Any length</option>
                  <option value="short">&lt; 50 km</option>
                  <option value="med">50–150 km</option>
                  <option value="long">150–300 km</option>
                  <option value="epic">300 km +</option>
                </select>
                <select value={routeCurv} onChange={e => setRouteCurv(e.target.value)}>
                  <option value="any">Any curviness</option>
                  <option value="flowing">Flowing +</option>
                  <option value="twisty">Twisty +</option>
                  <option value="very">Very twisty</option>
                </select>
              </div>
            </div>
          )}

          {!loading && !geoLoading && !error && displayedRoutes.length === 0 && (
            <div className="community__empty">
              <div className="community__empty-icon">{nearbyMode ? '📍' : '🗺'}</div>
              <div className="community__empty-title">
                {followingOnly ? 'No routes from people you follow yet'
                  : nearbyMode ? `No routes within ${maxDistKm ?? '∞'} km` : routeCountry ? 'No routes here yet' : 'No routes yet'}
              </div>
              <div className="community__empty-sub">
                {followingOnly ? 'Follow riders and businesses from their profiles to see their new routes here.'
                  : nearbyMode ? 'Try increasing the distance filter.'
                  : routeCountry ? 'Try another country, or “All countries”.'
                  : 'Be the first to publish a route!'}
              </div>
            </div>
          )}

          {!loading && !geoLoading && !error && displayedRoutes.length > 0 && (
            visibleRoutes.length === 0 ? (
              <div className="community__empty community__empty--mini">No routes match your filters.</div>
            ) : (
              <ul className="community__list">
                {visibleRoutes.map(route => {
                  const start  = cityOf(route.startName);
                  const end    = route.isLoop ? start : cityOf(route.endName);
                  const isOwn  = user?.uid === route.userId;
                  const distKm = route._distKm;
                  const hasImg = route.imageUrls?.length > 0;
                  const [g1, g2] = routeBanner(route.name);

                  return (
                    <li
                      key={route.id}
                      className="cc"
                      onClick={() => setSelected(route)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => e.key === 'Enter' && setSelected(route)}
                    >
                      {/* Banner */}
                      <div
                        className="cc__banner"
                        style={hasImg ? undefined : { background: `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)` }}
                      >
                        {hasImg
                          ? <img src={route.imageUrls[0]} alt="" loading="lazy" className="cc__banner-img" />
                          : (route.geometry
                              ? <RouteThumbnail geometry={route.geometry} width={170} height={96}
                                  stroke="rgba(255,255,255,0.92)" className="cc__banner-thumb" />
                              : <span className="cc__banner-initial">{route.name[0]?.toUpperCase()}</span>)
                        }
                        <div className="cc__banner-overlay" />

                        {/* Badges on banner */}
                        <div className="cc__banner-badges">
                          {route.isLoop
                            ? <span className="cc__badge cc__badge--loop">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
                                Loop
                              </span>
                            : <span className="cc__badge">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                                Route
                              </span>
                          }
                          {isOwn && <span className="cc__badge cc__badge--own">Yours</span>}
                        </div>

                        {/* Rating on banner */}
                        {route.ratingCount > 0 && (
                          <div className="cc__banner-rating">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                            {route.averageRating.toFixed(1)}
                          </div>
                        )}
                      </div>

                      {/* Card body */}
                      <div className="cc__body">
                        <div className="cc__name">{route.name}</div>

                        <div className="cc__path">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                          <span className="cc__city">{start || '—'}</span>
                          <span className="cc__arrow">{route.isLoop ? '↩' : '→'}</span>
                          <span className="cc__city">{end || '—'}</span>
                        </div>

                        <div className="cc__stats">
                          {route.distance != null && (
                            <span className="cc__stat">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.3 8.7 8.7 21.3c-1 1-2.5 1-3.4 0l-2.6-2.6c-1-1-1-2.5 0-3.4L15.3 2.7c1-1 2.5-1 3.4 0l2.6 2.6c1 1 1 2.5 0 3.4Z"/><path d="m7.5 10.5 2 2"/><path d="m10.5 7.5 2 2"/></svg>
                              {formatDistance(route.distance)}
                            </span>
                          )}
                          {route.duration != null && (
                            <span className="cc__stat">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                              {formatDuration(route.duration)}
                            </span>
                          )}
                          {nearbyMode && distKm != null && distKm !== Infinity && (
                            <span className="cc__stat cc__stat--dist">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>
                              {distKm < 1 ? `${Math.round(distKm * 1000)} m` : `${distKm < 10 ? distKm.toFixed(1) : Math.round(distKm)} km`} away
                            </span>
                          )}
                          <CurvinessBadge geometry={route.geometry} variant="inline" />
                        </div>

                        {/* Author row */}
                        <div className="cc__author">
                          {(route.business ? route.business.logoUrl : route.userPhoto)
                            ? <img src={route.business ? route.business.logoUrl : route.userPhoto} alt="" className="cc__avatar" />
                            : <span className="cc__avatar cc__avatar--initial">{(route.business?.name ?? route.userName)?.[0]?.toUpperCase() ?? '?'}</span>
                          }
                          <span className="cc__author-name">{route.business?.name ?? route.userName}</span>
                          <CreatorBadges badge={route.creatorBadge} />
                          {route.ratingCount > 0 && (
                            <span className="cc__author-rating">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="#f59e0b" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                              {route.averageRating.toFixed(1)}
                              <span className="cc__author-rating-count">({route.ratingCount})</span>
                            </span>
                          )}
                          <span className="cc__view-arrow">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )
          )}

          {!loading && !geoLoading && !error && !nearbyMode && !filterUser && !followingOnly && hasMore && (
            <button className="community__load-more" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? <><span className="community__spinner community__spinner--sm" /> Loading…</> : 'Load more'}
            </button>
          )}
        </>
      )}

      {selected && (
        <RouteDetailModal
          route={selected}
          onClose={() => setSelected(null)}
          onUnpublished={id => { handleUnpublished(id); setSelected(null); }}
          onExploreUser={(uid, name) => { setFilterUser({ uid, name }); setSelected(null); deactivateNearby(); }}
        />
      )}

      {selectedTour && (
        <TourDetailModal
          tour={selectedTour}
          onClose={() => setSelectedTour(null)}
          onCancelled={id => {
            setTours(prev => prev.filter(t => t.id !== id));
            setMyTours(prev => prev.filter(t => t.id !== id));
            setSelectedTour(null);
          }}
          onUpgrade={() => { setSelectedTour(null); onOpenProfile?.(); }}
          onEdit={t => { setEditTour(t); setShowTourBuilder(true); }}
          onCountChange={handleTourCountChange}
        />
      )}

      {showTourBuilder && (
        <ErrorBoundary onClose={() => { setShowTourBuilder(false); setEditTour(null); }}>
          <TourBuilder
            onClose={() => { setShowTourBuilder(false); setEditTour(null); }}
            onOpenProfile={onOpenProfile}
            editTour={editTour}
            onSaved={() => {
              // Refresh both lists after edit
              fetchPublishedTours().then(({ tours: t }) => setTours(t)).catch(() => {});
              if (user) fetchMyTours(user.uid).then(setMyTours).catch(() => {});
            }}
          />
        </ErrorBoundary>
      )}

      {showBusinessApply && user && (
        <BusinessApplicationModal
          uid={user.uid}
          initialData={profile || {}}
          onClose={() => setShowBusinessApply(false)}
          onSubmitted={() => showToast('Application submitted')}
        />
      )}
    </div>
  );
}

